import React, { useState, useEffect, useCallback } from 'react';
import useEscapeKey from '../utils/useEscapeKey';
import Footer from '../components/Footer';
import Select from '../components/Select';
import { useInvoice } from '../context/InvoiceContext';
import { vacationService, usersService } from '../services/api';

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];

// ── helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d) => d
  ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

function countWorkingDays(startStr, endStr, isHalfDay = false) {
  if (!startStr || !endStr) return 0;
  if (isHalfDay) return 0.5;
  const cur = new Date(startStr + 'T00:00:00Z');
  const end = new Date(endStr + 'T00:00:00Z');
  let count = 0;
  while (cur <= end) { count++; cur.setUTCDate(cur.getUTCDate() + 1); }
  return count;
}

const fmtDayCount = (n) => n === 0.5 ? '½' : n;

const userName = (u) => u ? [u.firstName, u.lastName].filter(Boolean).join(' ') : '—';

const TYPE_LABELS = { paid: 'Paid Leave', unpaid: 'Unpaid Leave', annual: 'Paid Leave', sick: 'Paid Leave' };

const STATUS_META = {
  pending:  { label: 'Pending',  color: '#D97706', bg: 'rgba(217,119,6,0.1)',  border: 'rgba(217,119,6,0.3)'  },
  approved: { label: 'Approved', color: '#16a34a', bg: 'rgba(22,163,74,0.1)',  border: 'rgba(22,163,74,0.3)'  },
  rejected: { label: 'Rejected', color: '#DC2626', bg: 'rgba(220,38,38,0.08)', border: 'rgba(220,38,38,0.25)' },
};

const thStyle = {
  padding: '8px 12px', textAlign: 'left',
  fontSize: '11px', fontWeight: '600',
  color: 'var(--color-text-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  borderBottom: '2px solid var(--color-gray-200)',
};
const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--color-gray-100)',
  fontSize: 'var(--font-size-sm)',
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '2px',
      fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em',
      color: m.color, background: m.bg, border: `1px solid ${m.border}`
    }}>
      {m.label}
    </span>
  );
};

// ── Calendar ──────────────────────────────────────────────────────────────────

const CalendarGrid = ({ year, month, vacationsByDate, onDayClick }) => {
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0 Sun=6

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div>
      {/* Day-of-week headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
        {DAYS.map(d => (
          <div key={d} style={{
            textAlign: 'center', fontSize: '11px', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.05em',
            padding: '6px 0',
            color: 'var(--color-text-secondary)',
          }}>
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={idx} />;

          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const vacs = vacationsByDate[dateStr] || [];
          const isToday = dateStr === todayStr;

          let bgColor = 'transparent';
          let textColor = 'var(--color-text-primary)';
          let fontWeight = isToday ? 700 : 400;

          if (vacs.length > 0) {
            if (vacs.some(v => v.status === 'approved')) {
              bgColor = 'rgba(22,163,74,0.13)';
              textColor = '#15803d';
            } else if (vacs.some(v => v.status === 'pending')) {
              bgColor = 'rgba(217,119,6,0.13)';
              textColor = '#b45309';
            }
          }

          return (
            <div
              key={idx}
              onClick={() => onDayClick(dateStr)}
              title={vacs.length > 0 ? vacs.map(v => `${v.name ? v.name + ': ' : ''}${TYPE_LABELS[v.type] || v.type} — ${v.status}`).join('\n') : undefined}
              style={{
                textAlign: 'center', padding: '8px 4px', borderRadius: '2px',
                background: bgColor, color: textColor,
                border: isToday ? '2px solid var(--color-primary)' : '1px solid transparent',
                cursor: 'pointer',
                fontSize: '13px', fontWeight,
                minHeight: '42px', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { if (vacs.length === 0) e.currentTarget.style.background = 'var(--color-gray-50)'; }}
              onMouseLeave={e => { if (vacs.length === 0) e.currentTarget.style.background = 'transparent'; }}
            >
              {day}
              {vacs.length > 0 && (
                vacs.length > 1
                  ? <span style={{ fontSize: '10px', fontWeight: 700, marginTop: '2px', lineHeight: 1, color: 'inherit' }}>{vacs.length}</span>
                  : <span style={{ display: 'block', width: '4px', height: '4px', borderRadius: '50%',
                      background: vacs[0].status === 'approved' ? '#16a34a' : '#d97706', marginTop: '2px' }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Request Modal ─────────────────────────────────────────────────────────────

const RequestModal = ({ onClose, onSave, defaultDate, balance, isIntern }) => {
  useEscapeKey(onClose);
  const [form, setForm] = useState({
    startDate: defaultDate || '',
    endDate: defaultDate || '',
    type: isIntern ? 'unpaid' : 'paid',
    notes: '',
    isHalfDay: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const workingDays = form.startDate && form.endDate && form.endDate >= form.startDate
    ? countWorkingDays(form.startDate, form.endDate, form.isHalfDay)
    : 0;

  const overBalance = form.type === 'paid' && balance && workingDays > balance.remaining;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (overBalance) return;
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        <div className="flex-between mb-lg">
          <h3>Request Leave</h3>
          <button onClick={onClose} className="btn btn-secondary btn-sm">✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-md">
            <div className="form-group">
              <label className="form-label">Start Date *</label>
              <input
                type="date" className="form-input" required
                value={form.startDate}
                onChange={e => setForm(f => ({
                  ...f,
                  startDate: e.target.value,
                  endDate: f.isHalfDay ? e.target.value : (e.target.value > f.endDate ? e.target.value : f.endDate),
                }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">End Date *</label>
              <input
                type="date" className="form-input" required
                min={form.startDate}
                value={form.endDate}
                disabled={form.isHalfDay}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                style={form.isHalfDay ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              />
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={form.isHalfDay}
                onChange={e => setForm(f => ({
                  ...f,
                  isHalfDay: e.target.checked,
                  endDate: e.target.checked ? f.startDate : f.endDate,
                }))}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>Half Day</span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Leave Type</label>
            <Select
              value={form.type}
              onChange={v => setForm(f => ({ ...f, type: v }))}
              options={isIntern
                ? [{ value: 'unpaid', label: 'Unpaid Leave' }]
                : [{ value: 'paid', label: 'Paid Leave' }, { value: 'unpaid', label: 'Unpaid Leave' }]
              }
            />
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-input" rows={2} style={{ resize: 'vertical' }}
              value={form.notes}
              placeholder="Any details for your manager…"
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </div>

          {workingDays > 0 && (
            <div style={{
              background: overBalance ? 'rgba(220,38,38,0.05)' : 'var(--color-gray-50)',
              border: `1px solid ${overBalance ? '#FCA5A5' : 'var(--color-gray-100)'}`,
              borderRadius: '2px', padding: '10px 14px',
              marginBottom: 'var(--spacing-md)',
              fontSize: '13px', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <strong>{fmtDayCount(workingDays)}</strong>&nbsp;working day{workingDays !== 1 ? 's' : ''}
              {form.type === 'paid' && balance && (
                <span style={{ color: overBalance ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>
                  &mdash; {fmtDayCount(balance.remaining)} day{balance.remaining !== 1 ? 's' : ''} remaining
                  {overBalance ? ' (exceeds balance)' : ''}
                </span>
              )}
            </div>
          )}

          {error && (
            <div style={{
              background: 'rgba(220,38,38,0.05)', border: '1px solid #FCA5A5',
              borderRadius: '2px', padding: '8px 12px',
              marginBottom: 'var(--spacing-md)',
              fontSize: '13px', color: 'var(--color-error)',
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button
              type="submit" className="btn btn-primary"
              disabled={saving || overBalance || !form.startDate || !form.endDate}
            >
              {saving ? 'Submitting…' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Reject Modal ──────────────────────────────────────────────────────────────

const RejectModal = ({ vacation, onClose, onReject }) => {
  useEscapeKey(onClose);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onReject(vacation.id, comment); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
        <div className="flex-between mb-lg">
          <h3>Reject Request</h3>
          <button onClick={onClose} className="btn btn-secondary btn-sm">✕</button>
        </div>
        <p style={{ marginBottom: 'var(--spacing-md)', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          Rejecting <strong>{userName(vacation.employee)}</strong>'s {TYPE_LABELS[vacation.type]}
          &nbsp;({fmtDate(vacation.startDate)} – {fmtDate(vacation.endDate)})
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Reason (optional)</label>
            <textarea
              className="form-input" rows={3} autoFocus
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Let the employee know why…"
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn btn-error">
              {saving ? 'Rejecting…' : 'Reject'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Leave Detail Modal (super_admin) ─────────────────────────────────────────

const LeaveDetailModal = ({ vacation, onClose, onSave }) => {
  useEscapeKey(onClose);
  const [type, setType] = useState(vacation.type);
  const [saving, setSaving] = useState(false);
  const changed = type !== vacation.type;

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(vacation.id, type); onClose(); }
    finally { setSaving(false); }
  };

  const days = countWorkingDays(vacation.startDate, vacation.endDate, vacation.isHalfDay);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
        <div className="flex-between mb-lg">
          <h3>Leave Details</h3>
          <button onClick={onClose} className="btn btn-secondary btn-sm">✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: 'var(--spacing-lg)', fontSize: '14px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Employee</div>
            <div style={{ fontWeight: 500 }}>{userName(vacation.employee)}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Status</div>
            <StatusBadge status={vacation.status} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>From</div>
            <div>{fmtDate(vacation.startDate)}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>To</div>
            <div>{fmtDate(vacation.endDate)}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Days</div>
            <div style={{ fontWeight: 600 }}>{fmtDayCount(days)} working day{days !== 1 ? 's' : ''}</div>
          </div>
          {vacation.notes && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Notes</div>
              <div style={{ color: 'var(--color-text-secondary)' }}>{vacation.notes}</div>
            </div>
          )}
        </div>

        <div className="form-group" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label className="form-label">Leave Type</label>
          <Select
            value={type}
            onChange={setType}
            options={[
              { value: 'paid', label: 'Paid Leave' },
              { value: 'unpaid', label: 'Unpaid Leave' },
            ]}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="btn btn-secondary">Close</button>
          {changed && (
            <button onClick={handleSave} disabled={saving} className="btn btn-primary">
              {saving ? 'Saving…' : 'Save Change'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Pending Approvals Card ────────────────────────────────────────────────────

const PendingApprovalsCard = ({ pendingAll, onApprove, onReject, onRowClick }) => (
  <div className="card" style={{ marginBottom: 'var(--spacing-xl)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--spacing-lg)' }}>
      <h2 style={{ fontSize: 'var(--font-size-lg)', margin: 0 }}>Pending Approvals</h2>
      {pendingAll.length > 0 && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 20, height: 20, borderRadius: '10px', padding: '0 6px',
          background: '#D97706', color: '#fff', fontSize: '11px', fontWeight: 700,
        }}>
          {pendingAll.length}
        </span>
      )}
    </div>

    {pendingAll.length === 0 ? (
      <div style={{ textAlign: 'center', padding: 'var(--spacing-xl) 0', color: 'var(--color-text-secondary)' }}>
        <div style={{ fontSize: '2rem', marginBottom: 8, opacity: 0.4 }}>✅</div>
        <p>No pending requests — all clear!</p>
      </div>
    ) : (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Employee</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>From</th>
              <th style={thStyle}>To</th>
              <th style={thStyle}>Days</th>
              <th style={thStyle}>Notes</th>
              <th style={{ ...thStyle, textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {pendingAll.map(v => (
              <tr key={v.id} onClick={() => onRowClick(v)} style={{ cursor: 'pointer' }}>
                <td style={{ ...tdStyle, fontWeight: 500 }}>
                  {userName(v.employee)}
                  {v.employee?.employeeNo && (
                    <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginLeft: 4 }}>
                      #{v.employee.employeeNo}
                    </span>
                  )}
                </td>
                <td style={tdStyle}>{TYPE_LABELS[v.type]}{v.isHalfDay && <span style={{ fontSize: '11px', marginLeft: 5, color: 'var(--color-text-secondary)', fontWeight: 400 }}>· Half Day</span>}</td>
                <td style={tdStyle}>{fmtDate(v.startDate)}</td>
                <td style={tdStyle}>{fmtDate(v.endDate)}</td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{fmtDayCount(countWorkingDays(v.startDate, v.endDate, v.isHalfDay))}</td>
                <td style={{ ...tdStyle, color: 'var(--color-text-secondary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={v.notes || ''}>
                  {v.notes || '—'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => onApprove(v.id)}
                      className="btn btn-sm"
                      style={{
                        background: 'rgba(22,163,74,0.1)', color: '#15803d',
                        border: '1px solid rgba(22,163,74,0.3)', fontWeight: 600,
                      }}
                    >
                      ✓ Approve
                    </button>
                    <button
                      onClick={() => onReject(v)}
                      className="btn btn-sm"
                      style={{
                        background: 'rgba(220,38,38,0.08)', color: '#DC2626',
                        border: '1px solid rgba(220,38,38,0.25)', fontWeight: 600,
                      }}
                    >
                      ✕ Reject
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const Vacation = () => {
  const { currentUser, isAdmin, showNotification } = useInvoice();
  const isManager = isAdmin();

  const today = new Date();
  const [calYear, setCalYear]   = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  const [selectedUserId, setSelectedUserId] = useState(currentUser?.role === 'super_admin' ? 'all' : currentUser?.id);
  const [users, setUsers]                   = useState([]);

  const [vacations, setVacations]   = useState([]);
  const [pendingAll, setPendingAll] = useState([]);
  const [balance, setBalance]       = useState(null);

  // Sync selectedUserId when impersonation starts/stops
  useEffect(() => {
    setSelectedUserId(currentUser?.role === 'super_admin' ? 'all' : currentUser?.id);
  }, [currentUser?.id, currentUser?.role]);

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [clickedDate, setClickedDate]           = useState(null);
  const [rejectTarget, setRejectTarget]         = useState(null);
  const [detailLeave, setDetailLeave]           = useState(null);
  const [loading, setLoading]                   = useState(true);

  // Load users for admin switcher
  useEffect(() => {
    if (!isManager) return;
    usersService.getUsers()
      .then(data => setUsers((data.users || []).filter(u => u.isActive)))
      .catch(() => {});
  }, [isManager]);

  const loadVacations = useCallback(async () => {
    setLoading(true);
    try {
      const isAll = selectedUserId === 'all';
      const [vacs, bal] = await Promise.all([
        vacationService.getAll({ year: calYear, userId: isAll ? undefined : selectedUserId }),
        isAll ? Promise.resolve(null) : vacationService.getBalance({ year: calYear, userId: selectedUserId }),
      ]);
      setVacations(vacs);
      setBalance(bal);

      if (isManager) {
        const pending = await vacationService.getAll({ status: 'pending' });
        setPendingAll(pending);
      }
    } catch (err) {
      showNotification(err.message || 'Failed to load vacations', 'error');
    } finally {
      setLoading(false);
    }
  }, [calYear, selectedUserId, isManager, showNotification]);

  useEffect(() => { loadVacations(); }, [loadVacations]);

  // Build dateStr → vacation map for the calendar
  const vacationsByDate = {};
  for (const v of vacations) {
    if (v.status === 'rejected') continue;
    const cur = new Date(v.startDate + 'T00:00:00Z');
    const end = new Date(v.endDate   + 'T00:00:00Z');
    while (cur <= end) {
      if (cur.getUTCFullYear() === calYear && cur.getUTCMonth() === calMonth) {
        const key = cur.toISOString().split('T')[0];
        if (!vacationsByDate[key]) vacationsByDate[key] = [];
        vacationsByDate[key].push({ status: v.status, type: v.type, name: userName(v.employee) });
      }
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
  }

  const prevMonth = () => calMonth === 0 ? (setCalMonth(11), setCalYear(y => y - 1)) : setCalMonth(m => m - 1);
  const nextMonth = () => calMonth === 11 ? (setCalMonth(0), setCalYear(y => y + 1)) : setCalMonth(m => m + 1);

  const handleDayClick = (dateStr) => {
    if (currentUser?.role === 'super_admin') return;
    setClickedDate(dateStr);
    setShowRequestModal(true);
  };

  const handleRequestSave = async (form) => {
    await vacationService.create({ ...form, userId: selectedUserId });
    showNotification('Leave request submitted', 'success');
    setShowRequestModal(false);
    setClickedDate(null);
    loadVacations();
  };

  const handleApprove = async (id) => {
    try {
      await vacationService.approve(id);
      showNotification('Request approved', 'success');
      loadVacations();
    } catch (err) {
      showNotification(err.message || 'Failed to approve', 'error');
    }
  };

  const handleReject = async (id, comment) => {
    await vacationService.reject(id, comment);
    showNotification('Request rejected', 'success');
    setRejectTarget(null);
    loadVacations();
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      await vacationService.delete(id);
      showNotification('Request cancelled', 'success');
      loadVacations();
    } catch (err) {
      showNotification(err.message || 'Failed to cancel', 'error');
    }
  };

  const handleLeaveTypeSave = async (id, type) => {
    try {
      await vacationService.update(id, { type });
      showNotification('Leave type updated', 'success');
      loadVacations();
    } catch (err) {
      showNotification(err.message || 'Failed to update', 'error');
    }
  };

  const myVacations = [...vacations].sort((a, b) => b.startDate.localeCompare(a.startDate));
  const viewingOwn  = selectedUserId === currentUser?.id;
  const selectedUser = users.find(u => u.id === selectedUserId);

  return (
    <>
      <main className="container" style={{ flex: 1, padding: 'var(--spacing-lg) var(--spacing-md)', width: '100%', boxSizing: 'border-box' }}>

  

        {/* ── Page title + action ── */}
        <div className="flex-between mb-xl" style={{ flexWrap: 'wrap', gap: 'var(--spacing-md)' }}>
          <div>
            <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, marginBottom: '4px' }}>Calendar</h1>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: 0 }}>
              1 day accrued per month of service
            </p>
          </div>
          {currentUser?.role !== 'super_admin' && (
            <button className="btn btn-primary" onClick={() => { setClickedDate(null); setShowRequestModal(true); }}>
              + Request Leave
            </button>
          )}
        </div>

        {/* ── Pending Approvals at top for super_admin ── */}
        {currentUser?.role === 'super_admin' && <PendingApprovalsCard pendingAll={pendingAll} onApprove={handleApprove} onReject={setRejectTarget} onRowClick={setDetailLeave} />}

        {/* ── Balance Cards ── */}
        {balance && selectedUserId !== 'all' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-xl)',
          }}>
            {[
              { label: 'Annual Allowance', value: balance.total,     unit: 'days', color: 'var(--color-text-primary)' },
              { label: 'Used',             value: balance.used,      unit: balance.used     !== 1 ? 'days' : 'day', color: '#16a34a' },
              { label: 'Pending',          value: balance.pending,   unit: balance.pending  !== 1 ? 'days' : 'day', color: '#D97706' },
              { label: 'Remaining',        value: balance.remaining, unit: balance.remaining !== 1 ? 'days' : 'day',
                color: balance.remaining === 0 ? 'var(--color-error)' : '#2563EB' },
            ].map(card => (
              <div key={card.label} className="card" style={{ textAlign: 'center', padding: 'var(--spacing-lg) var(--spacing-md)' }}>
                <div style={{ fontSize: '28px', fontWeight: 700, color: card.color, lineHeight: 1 }}>{fmtDayCount(card.value)}</div>
                <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '3px', fontWeight: 500 }}>{card.unit}</div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '6px' }}>{card.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Calendar Card ── */}
        <div className="card" style={{ marginBottom: 'var(--spacing-xl)' }}>

          {/* Calendar header: nav + legend + employee switcher */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }}>

            {/* Month navigation */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <button onClick={prevMonth} className="btn btn-secondary btn-sm" style={{ padding: '4px 12px', fontSize: '16px', lineHeight: 1 }}>‹</button>
              <span style={{ minWidth: '155px', textAlign: 'center', fontWeight: 600, fontSize: 'var(--font-size-lg)' }}>
                {MONTH_NAMES[calMonth]} {calYear}
              </span>
              <button onClick={nextMonth} className="btn btn-secondary btn-sm" style={{ padding: '4px 12px', fontSize: '16px', lineHeight: 1 }}>›</button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-lg)', flexWrap: 'wrap' }}>
              {/* Legend */}
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '2px', background: 'rgba(22,163,74,0.2)', border: '1px solid rgba(22,163,74,0.35)' }} />
                  Approved
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '2px', background: 'rgba(217,119,6,0.2)', border: '1px solid rgba(217,119,6,0.35)' }} />
                  Pending
                </span>
              </div>

              {/* Employee switcher (admin only) */}
              {isManager && users.length > 0 && (
                <Select
                  value={selectedUserId}
                  onChange={v => setSelectedUserId(v === 'all' ? 'all' : Number(v))}
                  options={[
                    ...(currentUser?.role === 'super_admin' ? [{ value: 'all', label: 'All Employees' }] : []),
                    { value: currentUser.id, label: 'My Calendar' },
                    ...users.filter(u => u.id !== currentUser.id).map(u => ({ value: u.id, label: userName(u) })),
                  ]}
                  style={{ width: 'auto', minWidth: '160px' }}
                />
              )}
            </div>
          </div>

          {loading
            ? <div className="flex-center" style={{ padding: 'var(--spacing-2xl)' }}><div className="spinner" /></div>
            : <CalendarGrid
                year={calYear} month={calMonth}
                vacationsByDate={vacationsByDate}
                onDayClick={handleDayClick}
              />
          }
        </div>

        {/* ── My / Selected User Requests ── */}
        <div className="card" style={{ marginBottom: 'var(--spacing-xl)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 'var(--spacing-lg)' }}>
            <h2 style={{ fontSize: 'var(--font-size-lg)', margin: 0 }}>
              {selectedUserId === 'all' ? 'All Requests' : viewingOwn ? 'My Requests' : `${userName(selectedUser)}'s Requests`}
            </h2>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{calYear}</span>
          </div>

          {myVacations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-xl) 0', color: 'var(--color-text-secondary)' }}>
              <div style={{ fontSize: '2rem', marginBottom: 8, opacity: 0.4 }}>🏖️</div>
              <p>No leave requests for {calYear}.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {selectedUserId === 'all' && <th style={thStyle}>Employee</th>}
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>From</th>
                    <th style={thStyle}>To</th>
                    <th style={thStyle}>Days</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Notes</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {myVacations.map(v => (
                    <tr
                      key={v.id}
                      onClick={currentUser?.role === 'super_admin' ? () => setDetailLeave(v) : undefined}
                      style={{ cursor: currentUser?.role === 'super_admin' ? 'pointer' : undefined }}
                    >
                      {selectedUserId === 'all' && (
                        <td style={{ ...tdStyle, fontWeight: 500 }}>{userName(v.employee)}</td>
                      )}
                      <td style={{ ...tdStyle, fontWeight: 500 }}>{TYPE_LABELS[v.type]}{v.isHalfDay && <span style={{ fontSize: '11px', marginLeft: 5, color: 'var(--color-text-secondary)', fontWeight: 400 }}>· Half Day</span>}</td>
                      <td style={tdStyle}>{fmtDate(v.startDate)}</td>
                      <td style={tdStyle}>{fmtDate(v.endDate)}</td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{fmtDayCount(countWorkingDays(v.startDate, v.endDate, v.isHalfDay))}</td>
                      <td style={tdStyle}><StatusBadge status={v.status} /></td>
                      <td style={{ ...tdStyle, color: 'var(--color-text-secondary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={v.notes || ''}>
                        {v.notes || '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                          {v.status === 'pending' && viewingOwn && (
                            <button onClick={() => handleCancel(v.id)} className="btn btn-secondary btn-sm">
                              Cancel
                            </button>
                          )}
                          {v.approvalComment && (
                            <span title={`Comment: ${v.approvalComment}`} style={{ cursor: 'help', fontSize: '14px' }}>
                              💬
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Pending Approvals at bottom for non-super_admin managers ── */}
        {isManager && currentUser?.role !== 'super_admin' && (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--spacing-lg)' }}>
              <h2 style={{ fontSize: 'var(--font-size-lg)', margin: 0 }}>Pending Approvals</h2>
              {pendingAll.length > 0 && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 20, height: 20, borderRadius: '10px', padding: '0 6px',
                  background: '#D97706', color: '#fff', fontSize: '11px', fontWeight: 700,
                }}>
                  {pendingAll.length}
                </span>
              )}
            </div>

            {pendingAll.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 'var(--spacing-xl) 0', color: 'var(--color-text-secondary)' }}>
                <div style={{ fontSize: '2rem', marginBottom: 8, opacity: 0.4 }}>✅</div>
                <p>No pending requests — all clear!</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Employee</th>
                      <th style={thStyle}>Type</th>
                      <th style={thStyle}>From</th>
                      <th style={thStyle}>To</th>
                      <th style={thStyle}>Days</th>
                      <th style={thStyle}>Notes</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingAll.map(v => (
                      <tr key={v.id}>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>
                          {userName(v.employee)}
                          {v.employee?.employeeNo && (
                            <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginLeft: 4 }}>
                              #{v.employee.employeeNo}
                            </span>
                          )}
                        </td>
                        <td style={tdStyle}>{TYPE_LABELS[v.type]}{v.isHalfDay && <span style={{ fontSize: '11px', marginLeft: 5, color: 'var(--color-text-secondary)', fontWeight: 400 }}>· Half Day</span>}</td>
                        <td style={tdStyle}>{fmtDate(v.startDate)}</td>
                        <td style={tdStyle}>{fmtDate(v.endDate)}</td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{fmtDayCount(countWorkingDays(v.startDate, v.endDate, v.isHalfDay))}</td>
                        <td style={{ ...tdStyle, color: 'var(--color-text-secondary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={v.notes || ''}>
                          {v.notes || '—'}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => handleApprove(v.id)}
                              className="btn btn-sm"
                              style={{
                                background: 'rgba(22,163,74,0.1)', color: '#15803d',
                                border: '1px solid rgba(22,163,74,0.3)', fontWeight: 600,
                              }}
                            >
                              ✓ Approve
                            </button>
                            <button
                              onClick={() => setRejectTarget(v)}
                              className="btn btn-sm"
                              style={{
                                background: 'rgba(220,38,38,0.08)', color: '#DC2626',
                                border: '1px solid rgba(220,38,38,0.25)', fontWeight: 600,
                              }}
                            >
                              ✕ Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </main>

      <Footer />

      {/* ── Modals ── */}
      {showRequestModal && (
        <RequestModal
          defaultDate={clickedDate}
          balance={balance}
          isIntern={currentUser?.employmentType === 'intern'}
          onClose={() => { setShowRequestModal(false); setClickedDate(null); }}
          onSave={handleRequestSave}
        />
      )}
      {rejectTarget && (
        <RejectModal
          vacation={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onReject={handleReject}
        />
      )}
      {detailLeave && (
        <LeaveDetailModal
          vacation={detailLeave}
          onClose={() => setDetailLeave(null)}
          onSave={handleLeaveTypeSave}
        />
      )}
    </>
  );
};

export default Vacation;
